import { get } from "svelte/store"
import { login, newNotification } from "./lib/store"
import { apiClientBaseURL } from "../../api/hooks/config-client"
import { obj2str } from "../../api/hooks/lib/utils"
import { spaNavigate } from "./lib/actions"
import { jwtDecode } from "jwt-decode"
/**
 * api request via client or server
 * server function ssrRequest is called via context.ssrRequest, binded in ssr hook
 *
 * @param {string} endpoint
 * @param {ApiOptions} options
 * @param {any} body
 * @returns {Promise<ApiResult<any>>}
 */
function apiRequest(endpoint: string, options: ApiOptions, body: any) {
    let method = options?.method || "GET"

    let query = "&count=1"
    if (options?.filter) {
        if (options.asExtendedFilter) {
            query += "&extendedFilter=" + encodeURIComponent(JSON.stringify(options.filter))
        } else query += "&filter=" + encodeURIComponent(JSON.stringify(options.filter))
    }
    if (options?.sort) query += "&sort=" + options.sort + "&sort=_id"
    if (options?.limit) query += "&limit=" + options.limit
    if (options?.offset) query += "&offset=" + options.offset
    if (options?.projection) query += "&projection=" + options.projection
    if (options?.lookup) query += "&lookup=" + options.lookup

    if (options?.params) {
        Object.keys(options.params).forEach((p) => {
            query += "&" + p + "=" + encodeURIComponent(options.params[p])
        })
    }

    /** @type {{[key: string]: string}} */
    let headers = {
        "Content-Type": "application/json",
    }

    if (options?.headers) headers = { ...headers, ...options.headers }

    let url = (endpoint.startsWith("/") ? "" : apiClientBaseURL) + endpoint + (query ? "?" + query : "")

    const requestOptions: RequestInit = {
        method,
        mode: "cors" as RequestMode,
        headers,
    }

    if (method === "POST" || method === "PUT") {
        requestOptions.body = JSON.stringify(body)
    }

    const response = fetch(url, requestOptions)
        .then((response) => {
            const contentType = response?.headers.get("Content-Type")
            return (contentType?.includes("application/json") ? response?.json() : response?.blob()).then((json) => {
                if (response?.status < 200 || response?.status >= 400) {
                    if (response?.status === 401) {
                        login.set(null)
                        if (!location.pathname.includes("login")) {
                            spaNavigate("/login")
                            newNotification({
                                html: "Nicht autorisiert. Bitte melde dich sich erneut an.",
                                class: "error",
                            })
                        }
                    }
                    if (response?.status === 403) {
                        /*return Promise.resolve(
                            getLogin()
                                .then(() => {
                                    console.log("huh?", response)
                                    apiRequest(endpoint, options, body)
                                })
                                .catch((e) => {
                                    console.log("huh?", response)
                                    spaNavigate("/login")
                                })
                        )*/
                        login.set(null)
                        if (!location.pathname.includes("login")) {
                            spaNavigate("/login")
                            newNotification({
                                html: "Nicht autorisiert. Bitte melde dich sich erneut an.",
                                class: "error",
                            })
                        }
                    }

                    return Promise.reject({ response, data: json })
                }

                return Promise.resolve({ data: json || null, count: response.headers?.get("x-results-count") || 0 })
            })
        })
        .catch((error) => {
            if (error.status === 401) {
                login.set(null)
                spaNavigate("/login")
                newNotification({
                    html: "Nicht autorisiert. Bitte melde dich sich erneut an.",
                    class: "error",
                })
            } else throw error
        })

    // @ts-ignore
    return response
}
export const api = async <T>(
    endpoint: string,
    options?: ApiOptions,
    body?: any
): Promise<{ data: T; count: number } | any> => {
    if (!options.headers) options.headers = {}
    if (options.method) options.method = options.method.toUpperCase()

    if (options.useJwt) {
        const login = await getLogin()
        options.headers.Authorization = `Bearer ${login?.tokenString}`
    }
    let data = await apiRequest(endpoint, options, body)
    return data
}
let refreshingToken: Promise<Login> | null
export const getLogin = async (signal?: AbortSignal): Promise<Login | null> => {
    let _l = get(login)

    if ((_l?.tokenData?.exp || 0) < new Date().getTime() / 1000) {
        // token expired, relogin via refresh cookie, run only one in parallel
        if (!refreshingToken)
            refreshingToken = postLogin({
                username: null,
                password: null,
                rememberLogin: true,
            })

        try {
            _l = await refreshingToken
        } catch {
            login.set(null)
            spaNavigate("/login")
        } finally {
            refreshingToken = null
        }
    }

    return _l
}
// init login state
getLogin().catch((e) => {
    login.set(null)
    spaNavigate("/login")
})
const cache: {
    [key: string]: {
        expire: number
        data: any
    }
} = {}

type EntryTypeSwitch<T> = T extends "media"
    ? Media
    : T extends "customer"
    ? Customer
    : T extends "equipment"
    ? Equipment
    : T extends "option"
    ? Option
    : T extends "user"
    ? User
    : T extends "vehicle"
    ? Vehicle
    : T extends "vehicleCategory"
    ? VehicleCategory
    : T extends "author"
    ? Author
    : T extends "login"
    ? Login
    : T extends "order"
    ? Order
    : T extends "task"
    ? TaskOilSpillRemoval | TaskTowing
    : T extends "taskStepsConfig"
    ? TaskStepsConfig
    : T extends "taskManipulationState"
    ? TaskManipulationState
    : T extends "protocol"
    ? Protocol
    : T extends "action"
    ? any
    : never

export async function getDBEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string,
    asExtendedFilter: boolean = false
): Promise<EntryTypeSwitch<T>[]> {
    const c = await api<EntryTypeSwitch<T>[]>(collectionName, {
        filter,
        sort,
        limit,
        offset,
        projection,
        useJwt: true,
        jwtToUse: get(login)?.tokenString,
        asExtendedFilter: asExtendedFilter,
    })
    return c.data
}

export async function getCachedEntries<T extends CollectionName>(
    collectionName: T,
    filter?: { [key: string]: any },
    sort: string = "sort",
    limit?: number,
    offset?: number,
    projection?: string
): Promise<EntryTypeSwitch<T>[]> {
    const filterStr = obj2str({ collectionName, filter, sort, limit, offset, projection })
    if (cache[filterStr] && cache[filterStr].expire >= Date.now()) {
        return cache[filterStr].data
    }
    const entries = await getDBEntries<T>(collectionName, filter, sort, limit, offset, projection)
    const inOneHour = Date.now() + 1000 * 60 * 60
    cache[filterStr] = { expire: inOneHour, data: entries }
    return entries
}

export async function getDBEntry<T extends CollectionName>(collectionName: T, filter: { [key: string]: any }) {
    return (await getDBEntries<T>(collectionName, filter, "_id"))?.[0]
}

export async function getCachedEntry<T extends CollectionName>(collectionName: T, filter: { [key: string]: any }) {
    return (await getCachedEntries<T>(collectionName, filter, "_id"))?.[0]
}

export async function postDBEntry<T extends CollectionName>(collectionName: T, entry: EntryTypeSwitch<T>) {
    return api<EntryTypeSwitch<T>>(
        collectionName,
        { method: "POST", useJwt: true, jwtToUse: get(login)?.tokenString },
        entry
    )
}

export async function putDBEntry<T extends CollectionName>(collectionName: T, entry: Partial<EntryTypeSwitch<T>>) {
    return api<EntryTypeSwitch<T>>(
        collectionName + "/" + entry.id,
        { method: "PUT", useJwt: true, jwtToUse: get(login)?.tokenString },
        entry
    )
}

export async function deleteDBEntry<T extends CollectionName>(collectionName: T, id: string) {
    return api<EntryTypeSwitch<T>>(collectionName + "/" + id, {
        method: "DELETE",
        useJwt: true,
        jwtToUse: get(login)?.tokenString,
    })
}

export async function postLogin(loginDetails: LoginInput): Promise<Login> {
    const r = await api<ServerLogin>(
        "login",
        {
            method: "POST",
        },
        loginDetails
    ).catch(() => spaNavigate("/login"))
    if (r?.data?.token) {
        const _l: Login = {
            created: new Date(r.data.created),
            tokenString: r.data.token,
            tokenData: jwtDecode<JWTLoginClaims>(r.data.token),
            user: r.data.user,
        }
        login.set(_l)

        return _l
    }
    throw new Error("token not found")
}
